import MapboxDraw from "@mapbox/mapbox-gl-draw";

import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, InputAdornment, TextField, useMediaQuery, useTheme } from "@mui/material";
import debounce from "lodash.debounce";
import mapboxgl from "mapbox-gl";

import "mapbox-gl/dist/mapbox-gl.css";

import { useCallback, useEffect, useRef, useState } from "react";

import { centroid } from "@turf/turf";

const INITIAL_POSITION = {
  latitude: 37.0902,
  longitude: -95.7129,
};
const INITIAL_ZOOM = 12;

export default function MapViewer({ geojson, onChange, onReload, onMapDelete }) {
  const isMedium = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const theme = useTheme();
  const mapContainerRef = useRef();
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  const [locationInput, setLocationInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const handleClear = () => {
    setLocationInput("");
    setSuggestions([]);
    onReload();
    goToCenter();
  };

  useEffect(() => {
    return () => {
      debouncedHandleLocationChange.cancel(); // Cancels any pending invocation
    };
  }, []);

  useEffect(() => {
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-streets-v11",
      center: [INITIAL_POSITION.longitude, INITIAL_POSITION.latitude],
      zoom: INITIAL_ZOOM,
      doubleClickZoom: false,
    });

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      defaultMode: "simple_select",
    });

    mapRef.current.addControl(drawRef.current);
    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current.on("load", () => {
      mapRef.current.addSource("geodata", {
        type: "geojson",
        data: drawRef.current.getAll(),
      });
      mapRef.current.addLayer({
        id: "highlighted-polygon",
        type: "fill",
        source: "geodata",
        paint: {
          "fill-color": theme.palette.colors.highlightLime,
          "fill-opacity": 0.6,
          "fill-outline-color": theme.palette.colors.alertRed,
        },
      });
      setIsLoading(false);
    });

    return () => {
      mapRef.current.remove();
    };
  }, []);

  useEffect(() => {
    const updateGeoJson = () => {
      const data = drawRef.current.getAll();
      if (onChange) onChange(data);
    };

    let selectedFeature = null;
    // Store selected feature when user selects it
    mapRef.current.on("draw.selectionchange", (e) => {
      if (e.features.length > 0) {
        selectedFeature = e.features[0];
      }
    });

    // Delete only the stored feature
    const deleteGeoJson = () => {
      if (!drawRef.current) return;
      if (!selectedFeature) {
        alert("No feature selected!");
        return;
      }
      drawRef.current.delete(selectedFeature.id);
      onMapDelete(selectedFeature);
      selectedFeature = null;
    };

    mapRef.current.on("draw.create", updateGeoJson);
    mapRef.current.on("draw.update", updateGeoJson);
    mapRef.current.on("draw.delete", deleteGeoJson);

    return () => {
      mapRef.current.off("draw.create", updateGeoJson);
      mapRef.current.off("draw.update", updateGeoJson);
      mapRef.current.off("draw.delete", deleteGeoJson);
    };
  }, [onChange, onMapDelete]);

  const handleLocationChange = useCallback(async (e) => {
    const input = e.target.value;
    setLocationInput(input);

    if (input.length < 3) {
      setSuggestions([]);
      return;
    }

    // Check if input is a pair of latitude and longitude
    const latLngRegex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
    if (latLngRegex.test(input)) {
      const [latitude, longitude] = input.split(",").map(Number);
      if (!isNaN(latitude) && !isNaN(longitude)) {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_MAPBOX_ENDPOINT}${longitude},${latitude}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}`
          );
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            setSuggestions(data.features); // Show suggestions for reverse geocoding result
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error("Error fetching reverse geocoding data:", error);
        }
      }
      return;
    }

    // Forward geocoding for address or place name
    try {
      const response = await fetch(
        `${import.meta.env.VITE_MAPBOX_ENDPOINT}${encodeURIComponent(
          input.trim()
        )}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}&autocomplete=true`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        setSuggestions(data.features); // Show suggestions for forward geocoding
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching forward geocoding data:", error);
    }
  }, []);
  const debouncedHandleLocationChange = useRef(debounce(handleLocationChange, 500)).current;

  const handleInputChange = (e) => {
    setLocationInput(e.target.value);
    debouncedHandleLocationChange(e);
  };

  const handleSuggestionClick = (longitude, latitude, placeName) => {
    setLocationInput(placeName);
    setSuggestions([]);
    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: INITIAL_ZOOM,
    });
  };

  useEffect(() => {
    if (isLoading || !mapRef.current.getSource("geodata")) return;
    drawRef.current.deleteAll();
    if (geojson) {
      drawRef.current.add(geojson);
    }
    mapRef.current.getSource("geodata").setData(drawRef.current.getAll());
    if (geojson) {
      goToCenter();
    }
  }, [geojson, isLoading]);

  const goToCenter = () => {
    const feature = drawRef.current.getAll()?.features?.[0] ?? false;
    if (feature && locationInput === "" && mapRef.current) {
      const center = centroid(feature);
      mapRef.current.flyTo({
        center: center.geometry.coordinates,
        zoom: INITIAL_ZOOM,
      });
    }
  };

  return (
    <div style={{ width: isMedium ? "auto" : "500px" }}>
      <div style={{ marginBottom: "10px", width: "100%", position: "relative" }}>
        <TextField
          style={{ width: "100%" }}
          variant="outlined"
          value={locationInput}
          onChange={handleInputChange}
          placeholder="Enter an address or 'latitude,longitude'"
          InputProps={{
            endAdornment: locationInput && (
              <InputAdornment position="end">
                <IconButton onClick={handleClear} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {suggestions.length > 0 && (
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              margin: "5px 0 0 0",
              border: "1px solid var(--mui-palette-divider)",
              borderRadius: "4px",
              backgroundColor: "var(--mui-palette-background-paper)",
              maxHeight: "150px",
              overflowY: "auto",
              position: "absolute",
              left: 0,
              right: 0,
              zIndex: 1000,
            }}
          >
            {suggestions.map((s) => (
              <li
                key={s.id}
                onClick={() => handleSuggestionClick(s.center[0], s.center[1], s.place_name)}
                style={{
                  padding: "8px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--mui-palette-grey-200)",
                }}
              >
                {s.place_name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div
        ref={mapContainerRef}
        style={{ width: isMedium ? "auto" : "500px", height: isMedium ? "400px" : "500px" }}
      ></div>
    </div>
  );
}
